import React, { useState } from 'react';
import { supabase } from '../../../utils/supabase';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import RatingStars from '../../../components/ui/RatingStars';
import Icon from '../../../components/AppIcon';

/**
 * Form for employers to submit reviews for their VAs
 * Reviews require admin approval before appearing on VA profiles
 */
const ReviewForm = ({ vaId, contractId, applicationId, onSuccess }) => {
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { userProfile } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0 || rating < 1 || rating > 5) {
      setError('Please select a valid rating (1-5 stars)');
      return;
    }

    if (!content.trim()) {
      setError('Please write a review');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Insert review with admin_approved set to false by default
      const { data, error: insertError } = await supabase
        .from('reviews')
        .insert({
          employer_id: userProfile.id,
          va_id: vaId,
          contract_id: contractId,
          application_id: applicationId,
          rating,
          content: content.trim(),
          review_text: content.trim(), // Also store in review_text field
          is_public: isPublic,
          admin_approved: false, // Reviews must be approved by admin before appearing on VA profile
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Success - show message about admin approval
      alert('✅ Review submitted successfully!\n\nYour review will appear on the VA\'s profile after admin approval. You can view your pending review in your dashboard.');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Info Banner about Admin Approval */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Reviews require admin approval</p>
          <p className="text-blue-700">
            Your review will be submitted for approval and will appear on the VA's profile once verified by our admin team.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">
          Rating
        </label>
        <RatingStars
          value={rating}
          onChange={setRating}
          size="lg"
          readonly={false}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">
          Review
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          rows={4}
          placeholder="Share your experience working with this VA..."
          required
        />
      </div>
      
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isPublic"
          checked={isPublic}
          onChange={(e) => setIsPublic(e.target.checked)}
          className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
        />
        <label htmlFor="isPublic" className="ml-2 block text-sm text-text-primary">
          Make this review public on the VA's profile (after admin approval)
        </label>
      </div>
      
      {error && (
        <div className="text-error text-sm">{error}</div>
      )}
      
      <div className="flex justify-end space-x-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => onSuccess()}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isSubmitting}
        >
          Submit Review
        </Button>
      </div>
    </form>
  );
};

export default ReviewForm;


